import { css } from "@emotion/react";
import type { ReactNode } from "react";

import type { Action } from "../../action/Action";
import Link from "../../action/Link";
import { Asset } from "../Asset";
import Icon from "../Icon";
import type { IconName } from "../Icon/types";
import { getSpaceVariable } from "../space/utilities";
import { WithSpace } from "../space/WithSpace";
import Text from "../Text";
import { useTheme } from "../theme/hooks";

const MEDIA_SIZE = getSpaceVariable(8);

const mediaCss = css({
    width: MEDIA_SIZE,
    height: MEDIA_SIZE,
    borderRadius: "var(--rs-radius-small)",
    overflow: "hidden",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
});

type ListItemProps = {
    label: string;
    help: ReactNode;
    action?: Action;
    icon?: IconName;
    image?: string;
};

export default function ListItem(props: ListItemProps) {
    const { label, help, action, icon, image } = props;
    const content = (
        <>
            <Text variant="body">{label}</Text>
            <Text variant="minor">{help}</Text>
        </>
    );
    let leading = null;
    if (image) {
        leading = <ListItemImage src={image} />;
    } else if (icon) {
        leading = <ListItemIcon name={icon} />;
    }
    return (
        <Link action={action}>
            <WithSpace horizontal={4} vertical={2}>
                {leading ? (
                    <div
                        css={css({
                            display: "flex",
                            alignItems: "center",
                            gap: getSpaceVariable(3),
                        })}
                    >
                        {leading}
                        <div css={{ flex: 1, minWidth: 0 }}>{content}</div>
                    </div>
                ) : (
                    content
                )}
            </WithSpace>
        </Link>
    );
}

function ListItemImage(props: { src: string }) {
    const { src } = props;
    return (
        <div css={mediaCss}>
            <Asset
                src={src}
                alt=""
                css={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                }}
            />
        </div>
    );
}

function ListItemIcon(props: { name: IconName }) {
    const { name } = props;
    const theme = useTheme();
    return (
        <div
            css={[
                mediaCss,
                css({
                    backgroundColor: theme.divider,
                    padding: getSpaceVariable(1),
                    boxSizing: "border-box",
                    "& svg": {
                        width: "100%",
                        height: "100%",
                    },
                }),
            ]}
        >
            <Icon name={name} />
        </div>
    );
}
